import React, { useState, createElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, DownloadIcon, ZoomInIcon, ZoomOutIcon, PrinterIcon } from 'lucide-react';
import { useTheme } from '../providers/ThemeProvider';
import { Navbar } from '../Navbar';
import { Footer } from '../Footer';
export const ToccatinaViewer: React.FC = () => {
  const navigate = useNavigate();
  const {
    theme
  } = useTheme();
  const isDark = theme === 'dark';
  const [zoom, setZoom] = useState(100);
  // Detect mobile device
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  // PDF URL for Toccatina
  const pdfUrl = 'https://www.mutopiaproject.org/ftp/AlkanCV/O75/toccatina/toccatina-let.pdf';
  // Use mobile-friendly PDF parameters on mobile devices
  const pdfViewUrl = isMobile ? `${pdfUrl}#view=Fit&zoom=page-width` : `${pdfUrl}#view=FitH`;
  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 10, 200));
  };
  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 10, 50));
  };
  const handleDownload = () => {
    // Download the actual PDF
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = 'Alkan - Toccatina.pdf';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const handlePrint = () => {
    window.print();
  };
  return <div className={`min-h-screen ${isDark ? 'bg-gradient-to-r from-[#0F1626] via-[#1E3A8A] to-[#2C3E50]' : 'bg-gray-50'}`} data-id="element-10457">
      <Navbar data-id="element-10458" />

      {/* Wrapper with top padding to account for fixed Navbar */}
      <div className="pt-20" data-id="element-10459">
        {/* Hero Banner with Image */}
        <div className="relative w-full" style={{
        minHeight: '320px'
      }} data-id="element-10460">
          <img src="https://images.unsplash.com/photo-1520523839897-bd0b52f945a0?w=1200&h=400&fit=crop" alt="Toccatina" className="w-full h-64 md:h-80 object-cover" data-id="element-10461" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/30 to-black/70" data-id="element-10462" />
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8" data-id="element-10463">
            <div className="max-w-7xl mx-auto" data-id="element-10464">
              <button onClick={() => navigate(-1)} className="mb-4 flex items-center text-white/90 hover:text-white transition-colors" data-id="element-10465">
                <ArrowLeftIcon className="h-5 w-5 mr-2" data-id="element-10466" />
                Back to Library
              </button>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-2" data-id="element-10467">
                Toccatina
              </h1>
              <p className="text-lg md:text-xl text-white/90" data-id="element-10468">Op. 75</p>
              <p className="text-base md:text-lg text-white/80 mt-1" data-id="element-10469">
                Charles-Valentin Alkan
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className={`sticky top-20 z-10 ${isDark ? 'bg-navy-800/95 backdrop-blur-sm border-b border-white/10' : 'bg-white/95 backdrop-blur-sm border-b border-gray-200'} shadow-sm`} data-id="element-10470">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between" data-id="element-10471">
          <div className="flex items-center space-x-2" data-id="element-10472">
            {!isMobile && <>
                <button onClick={handleZoomOut} className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-navy-700 text-white' : 'hover:bg-gray-100 text-gray-800'}`} aria-label="Zoom out" data-id="element-10473">
                  <ZoomOutIcon className="h-5 w-5" data-id="element-10474" />
                </button>
                <span className={`text-sm font-medium px-3 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-10475">
                  {zoom}%
                </span>
                <button onClick={handleZoomIn} className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-navy-700 text-white' : 'hover:bg-gray-100 text-gray-800'}`} aria-label="Zoom in" data-id="element-10476">
                  <ZoomInIcon className="h-5 w-5" data-id="element-10477" />
                </button>
              </>}
          </div>
          <div className="flex items-center space-x-2" data-id="element-10478">
            <button onClick={handlePrint} className={`flex items-center justify-center px-4 py-2 rounded-lg transition-colors ${isDark ? 'hover:bg-navy-700 text-white border border-navy-700' : 'hover:bg-gray-100 text-gray-800 border border-gray-200'}`} data-id="element-10479">
              <PrinterIcon className="h-4 w-4 sm:mr-2" data-id="element-10480" />
              <span className="hidden sm:inline" data-id="element-10481">Print</span>
            </button>
            <button onClick={handleDownload} className="flex items-center justify-center px-4 py-2 border-2 border-green-500 text-green-500 bg-transparent hover:bg-green-500/10 rounded-lg transition-colors" data-id="element-10482">
              <DownloadIcon className="h-4 w-4 sm:mr-2" data-id="element-10483" />
              <span className="hidden sm:inline" data-id="element-10484">Download PDF</span>
            </button>
          </div>
        </div>
      </div>

      {/* Sheet Music Viewer */}
      <main className="max-w-7xl mx-auto px-4 py-8" data-id="element-10485">
        <div className={`rounded-xl overflow-hidden shadow-lg ${isDark ? 'bg-navy-800' : 'bg-white'}`} data-id="element-10486">
          {/* PDF Viewer - Embedded */}
          <div className="w-full" style={{
          height: 'calc(100vh - 300px)',
          minHeight: '600px'
        }} data-id="element-10487">
            <iframe src={pdfViewUrl} className="w-full h-full" title="Toccatina Sheet Music" style={{
            border: 'none',
            ...(isMobile ? {} : {
              transform: `scale(${zoom / 100})`,
              transformOrigin: 'top center',
              transition: 'transform 0.2s ease'
            })
          }} data-id="element-10488" />
          </div>
        </div>

        {/* Piece Information */}
        <div className={`mt-8 rounded-xl p-6 ${isDark ? 'bg-navy-800' : 'bg-white'} shadow-sm`} data-id="element-10489">
          <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-10490">
            About This Piece
          </h2>
          <div className="grid md:grid-cols-2 gap-6" data-id="element-10491">
            <div data-id="element-10492">
              <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-10493">
                Composition Details
              </h3>
              <dl className="space-y-2" data-id="element-10494">
                <div data-id="element-10495">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-10496">
                    Composer
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-10497">
                    Charles-Valentin Alkan
                  </dd>
                </div>
                <div data-id="element-10498">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-10499">
                    Year Composed
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-10500">
                    1872
                  </dd>
                </div>
                <div data-id="element-10501">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-10502">
                    Key
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-10503">
                    G Major
                  </dd>
                </div>
                <div data-id="element-10504">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-10505">
                    Opus
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-10506">
                    Op. 75
                  </dd>
                </div>
              </dl>
            </div>
            <div data-id="element-10507">
              <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-10508">
                Performance Notes
              </h3>
              <p className={`text-sm leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-10509">
                The Toccatina, Op. 75, is a brilliant and virtuosic work by
                Charles-Valentin Alkan, one of the most technically demanding
                composers of the Romantic era. This piece showcases rapid
                fingerwork, intricate passages, and the characteristic
                brilliance that defines Alkan's compositional style. Despite its
                technical challenges, the Toccatina maintains a playful and
                energetic character throughout, making it a favorite among
                advanced pianists seeking to demonstrate both technical prowess
                and musical sensitivity.
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer data-id="element-10510" />
    </div>;
};